'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BadgeCheck, CalendarCheck2, Users } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { HeroBanner } from '@/components/ui/HeroBanner';
import { SectionsBlock } from '@/components/cms/SectionRenderer';
import { getPage, type HeroBannerSettings, type Page, type SectionItemPayload } from '@/lib/cms';
import { resourceHubApi } from '@/lib/resource-hub-api';

type Story = {
  id: string;
  organization: string;
  event: string;
  challenge: string;
  solution: string;
  result: string;
};

function getString(input: unknown): string | null {
  return typeof input === 'string' && input.trim() ? input.trim() : null;
}

function mapItemToStory(item: SectionItemPayload): Story | null {
  const metadata = (item.metadata ?? {}) as Record<string, unknown>;
  const organization = getString(item.title);
  if (!organization) return null;

  return {
    id: String(item.id ?? organization),
    organization,
    event: getString(item.subtitle) || 'Customer story',
    challenge: getString(metadata.challenge) || 'Challenge details are being updated.',
    solution: getString(metadata.solution) || getString(item.description) || 'Solution details are being updated.',
    result: getString(metadata.result) || 'Outcome details are being updated.',
  };
}

export default function SuccessStoriesPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [apiStories, setApiStories] = useState<Story[]>([]);

  useEffect(() => {
    getPage('success-stories').then(setPage).catch(() => setPage(null));
    resourceHubApi
      .successStories()
      .then((response) => {
        const mapped = (response.data.data ?? []).map((row) => ({
          id: String(row.id),
          organization: row.organization_name,
          event: row.title,
          challenge: row.challenge || 'Challenge details are being updated.',
          solution: row.solution || 'Solution details are being updated.',
          result: row.result || 'Outcome details are being updated.',
        }));
        setApiStories(mapped);
      })
      .catch(() => setApiStories([]));
  }, []);

  const heroSection = page?.sections?.find((section) => section.type === 'hero_banner');
  const title = page?.title || 'Success Stories';
  const description =
    page?.content ||
    'Real organizers share the challenge they faced, what they changed, and the measurable outcomes they achieved.';

  const cmsStories = useMemo(() => {
    const items = (page?.sections ?? [])
      .filter((section) => section.type !== 'hero_banner')
      .flatMap((section) => section.items ?? []);

    return items.map(mapItemToStory).filter((item): item is Story => item !== null);
  }, [page]);

  const stories = apiStories.length ? apiStories : cmsStories;
  const outcomes = useMemo(() => {
    const sectionOutcomes = (page?.sections ?? [])
      .flatMap((section) => section.items ?? [])
      .map((item) => getString(item.description))
      .filter((value): value is string => Boolean(value));

    return sectionOutcomes.length ? sectionOutcomes.slice(0, 6) : [];
  }, [page]);

  return (
    <MainLayout>
      <div className="pb-20 space-y-10">
        {heroSection ? (
          <HeroBanner data={(heroSection.settings ?? {}) as HeroBannerSettings} />
        ) : (
          <section className="rounded-3xl border border-landing-border bg-gradient-to-br from-soot via-moss to-soot p-8 text-text-inverse sm:p-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-inverse/80">Success Stories</p>
            <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">{title}</h1>
            <p className="mt-4 max-w-2xl text-sm text-text-inverse/85 sm:text-base">{description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                href="/host-events"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-plaster px-5 py-3 text-sm font-semibold text-soot"
              >
                Start your event
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-white/35 px-5 py-3 text-sm font-semibold text-text-inverse"
              >
                Share your story
              </Link>
            </div>
          </section>
        )}

        {page?.sections && page.sections.length > 0 && <SectionsBlock sections={page.sections} />}

        <section className="grid gap-4 md:grid-cols-3">
          {[{ label: 'Events supported', value: '1,200+', icon: CalendarCheck2 }, { label: 'Attendees served', value: '500K+', icon: Users }, { label: 'Stories published', value: `${stories.length}+`, icon: BadgeCheck }].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="rounded-2xl border border-landing-border bg-bg p-5">
                <div className="inline-flex rounded-lg bg-landing-bg p-2 text-landing-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <p className="mt-3 text-2xl font-bold text-landing-text">{item.value}</p>
                <p className="mt-1 text-sm text-landing-muted">{item.label}</p>
              </div>
            );
          })}
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-landing-text">Customer stories</h2>
          <div className="grid gap-4 lg:grid-cols-3">
            {stories.length === 0 && (
              <article className="rounded-2xl border border-landing-border bg-bg p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold text-landing-text">No stories published yet</h3>
                <p className="mt-2 text-sm leading-relaxed text-landing-muted">Your success stories will appear here once published.</p>
              </article>
            )}
            {stories.map((story) => (
              <article key={story.id} className="rounded-2xl border border-landing-border bg-bg p-6">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-landing-primary">{story.organization}</p>
                <h3 className="mt-2 text-lg font-semibold text-landing-text">{story.event}</h3>
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-landing-muted">
                  <p>
                    <span className="font-semibold text-landing-text">Challenge:</span> {story.challenge}
                  </p>
                  <p>
                    <span className="font-semibold text-landing-text">Solution:</span> {story.solution}
                  </p>
                  <p>
                    <span className="font-semibold text-landing-text">Result:</span> {story.result}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-landing-border bg-bg p-6 sm:p-7">
          <h2 className="text-xl font-semibold text-landing-text">Common outcomes after switching to EventSphere</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {outcomes.length === 0 && (
              <div className="flex items-start gap-2 rounded-xl border border-landing-border bg-landing-bg/40 p-4 text-sm text-landing-muted sm:col-span-2">
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-landing-primary" />
                <p>Insights and outcome highlights will appear after stories are configured.</p>
              </div>
            )}
            {outcomes.map((outcome) => (
              <div key={outcome} className="flex items-start gap-2 rounded-xl border border-landing-border bg-landing-bg/40 p-4 text-sm text-landing-muted">
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-landing-primary" />
                <p>{outcome}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
